import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  startAfter, 
  limit,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import moment from 'moment';
import { calculateMaterialRequirements } from './inventoryCalculations';
import { getMaterialListByInvoice } from '../firebase/materialListService';

/**
 * Advanced Analytics Engine
 * Provides comprehensive analytics for material utilization, schedule optimization,
 * and business intelligence insights
 */

// Analytics time periods
export const TIME_PERIODS = {
  LAST_7_DAYS: 'last_7_days',
  LAST_30_DAYS: 'last_30_days',
  LAST_90_DAYS: 'last_90_days',
  LAST_6_MONTHS: 'last_6_months',
  LAST_YEAR: 'last_year',
  ALL_TIME: 'all_time',
  CUSTOM: 'custom'
};

// Analytics categories
export const ANALYTICS_CATEGORIES = {
  MATERIAL_UTILIZATION: 'material_utilization',
  SCHEDULE_OPTIMIZATION: 'schedule_optimization',
  FINANCIAL_PERFORMANCE: 'financial_performance',
  CUSTOMER_INSIGHTS: 'customer_insights',
  OPERATIONAL_EFFICIENCY: 'operational_efficiency'
};

// Enhanced analytics categories
export const ENHANCED_ANALYTICS_CATEGORIES = {
  ...ANALYTICS_CATEGORIES,
  HYBRID_MATERIAL_UTILIZATION: 'hybrid_material_utilization',
  MATERIAL_REVENUE_ANALYSIS: 'material_revenue_analysis',
  PROFITABILITY_BY_MATERIAL: 'profitability_by_material',
  INVOICE_TYPE_PERFORMANCE: 'invoice_type_performance',
  DATA_SOURCE_DISTRIBUTION: 'data_source_distribution',
  MATERIAL_LIST_ADOPTION: 'material_list_adoption'
};

// Material utilization metrics
export const UTILIZATION_METRICS = {
  AVAILABILITY_RATE: 'availability_rate',
  UTILIZATION_RATE: 'utilization_rate',
  TURNOVER_RATE: 'turnover_rate',
  IDLE_TIME: 'idle_time',
  MAINTENANCE_FREQUENCY: 'maintenance_frequency',
  DAMAGE_RATE: 'damage_rate'
};

/**
 * Get date range for analytics period
 * @param {string} period - Time period constant
 * @param {Object} customRange - Custom date range {startDate, endDate}
 * @returns {Object} Date range with start and end dates
 */
export const getAnalyticsDateRange = (period, customRange = null) => {
  const now = moment();
  let startDate, endDate;

  switch (period) {
    case TIME_PERIODS.LAST_7_DAYS:
      startDate = now.clone().subtract(7, 'days').startOf('day');
      endDate = now.clone().endOf('day');
      break;
    case TIME_PERIODS.LAST_30_DAYS:
      startDate = now.clone().subtract(30, 'days').startOf('day');
      endDate = now.clone().endOf('day');
      break;
    case TIME_PERIODS.LAST_90_DAYS:
      startDate = now.clone().subtract(90, 'days').startOf('day');
      endDate = now.clone().endOf('day');
      break;
    case TIME_PERIODS.LAST_6_MONTHS:
      startDate = now.clone().subtract(6, 'months').startOf('day');
      endDate = now.clone().endOf('day');
      break;
    case TIME_PERIODS.LAST_YEAR:
      startDate = now.clone().subtract(1, 'year').startOf('day');
      endDate = now.clone().endOf('day');
      break;
    case TIME_PERIODS.ALL_TIME:
      // For all-time, use a date far in the past (e.g., year 2000)
      startDate = moment('2000-01-01').startOf('day');
      endDate = now.clone().endOf('day');
      break;
    case TIME_PERIODS.CUSTOM:
      if (customRange) {
        startDate = moment(customRange.startDate).startOf('day');
        endDate = moment(customRange.endDate).endOf('day');
      } else {
        // Default to last 30 days if custom range not provided
        startDate = now.clone().subtract(30, 'days').startOf('day');
        endDate = now.clone().endOf('day');
      }
      break;
    default:
      startDate = now.clone().subtract(30, 'days').startOf('day');
      endDate = now.clone().endOf('day');
  }

  return {
    startDate: startDate.toDate(),
    endDate: endDate.toDate(),
    startDateString: startDate.format('YYYY-MM-DD'),
    endDateString: endDate.format('YYYY-MM-DD'),
    periodLabel: getPeriodLabel(period, customRange),
    isAllTime: period === TIME_PERIODS.ALL_TIME
  };
};

/**
 * Get human-readable label for time period
 * @param {string} period - Time period constant
 * @param {Object} customRange - Custom date range
 * @returns {string} Period label
 */
const getPeriodLabel = (period, customRange) => {
  switch (period) {
    case TIME_PERIODS.LAST_7_DAYS:
      return 'Last 7 Days';
    case TIME_PERIODS.LAST_30_DAYS:
      return 'Last 30 Days';
    case TIME_PERIODS.LAST_90_DAYS:
      return 'Last 90 Days';
    case TIME_PERIODS.LAST_6_MONTHS:
      return 'Last 6 Months';
    case TIME_PERIODS.LAST_YEAR:
      return 'Last Year';
    case TIME_PERIODS.ALL_TIME:
      return 'All Time';
    case TIME_PERIODS.CUSTOM:
      if (customRange) {
        return `${moment(customRange.startDate).format('MMM DD, YYYY')} - ${moment(customRange.endDate).format('MMM DD, YYYY')}`;
      }
      return 'Custom Period';
    default:
      return 'Last 30 Days';
  }
};

/**
 * Material Utilization Analytics
 * Analyzes how efficiently materials are being used
 */
export class MaterialUtilizationAnalytics {
  constructor(dateRange) {
    this.dateRange = dateRange;
  }

  /**
   * Get comprehensive material utilization report
   * @returns {Promise<Object>} Utilization report
   */
  async generateUtilizationReport() {
    try {
      console.log('📊 Generating material utilization report...');

      const [
        inventoryData,
        materialListData,
        scheduleData,
        invoiceData
      ] = await Promise.all([
        this.getInventoryData(),
        this.getMaterialListData(),
        this.getScheduleData(),
        this.getInvoiceData()
      ]);

      // Calculate utilization metrics
      const utilizationMetrics = this.calculateUtilizationMetrics(
        inventoryData,
        materialListData,
        scheduleData,
        invoiceData
      );

      // Generate insights and recommendations
      const insights = this.generateUtilizationInsights(utilizationMetrics);

      return {
        period: this.dateRange.periodLabel,
        dateRange: this.dateRange,
        metrics: utilizationMetrics,
        insights,
        materialBreakdown: this.getMaterialTypeBreakdown(inventoryData, materialListData),
        trends: this.calculateUtilizationTrends(utilizationMetrics),
        recommendations: this.generateRecommendations(utilizationMetrics, insights)
      };

    } catch (error) {
      console.error('❌ Error generating utilization report:', error);
      throw error;
    }
  }

  /**
   * Get inventory data for the period
   * @returns {Promise<Array>} Inventory data
   */
  async getInventoryData() {
    try {
      const inventoryQuery = query(
        collection(db, 'inventory'),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(inventoryQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error fetching inventory data:', error);
      return [];
    }
  }

  /**
   * Get material list data for the period
   * @returns {Promise<Array>} Material list data
   */
  async getMaterialListData() {
    try {
      const materialListQuery = query(
        collection(db, 'materialLists'),
        where('createdAt', '>=', Timestamp.fromDate(this.dateRange.startDate)),
        where('createdAt', '<=', Timestamp.fromDate(this.dateRange.endDate)),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(materialListQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error fetching material list data:', error);
      return [];
    }
  }

  /**
   * Get schedule data for the period
   * @returns {Promise<Array>} Schedule data
   */
  async getScheduleData() {
    try {
      const scheduleQuery = query(
        collection(db, 'schedules'),
        where('createdAt', '>=', Timestamp.fromDate(this.dateRange.startDate)),
        where('createdAt', '<=', Timestamp.fromDate(this.dateRange.endDate)),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(scheduleQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error fetching schedule data:', error);
      return [];
    }
  }

  /**
   * Get invoice data for the period
   * @returns {Promise<Array>} Invoice data
   */
  async getInvoiceData() {
    try {
      const invoiceQuery = query(
        collection(db, 'invoices'),
        where('createdAt', '>=', Timestamp.fromDate(this.dateRange.startDate)),
        where('createdAt', '<=', Timestamp.fromDate(this.dateRange.endDate)),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(invoiceQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error fetching invoice data:', error);
      return [];
    }
  }

  /**
   * Calculate utilization metrics
   * @param {Array} inventoryData - Inventory data
   * @param {Array} materialListData - Material list data
   * @param {Array} scheduleData - Schedule data
   * @param {Array} invoiceData - Invoice data
   * @returns {Object} Utilization metrics
   */
  calculateUtilizationMetrics(inventoryData, materialListData, scheduleData, invoiceData) {
    const totalInventoryItems = inventoryData.length;
    const totalMaterialRequests = materialListData.length;
    const totalSchedules = scheduleData.length;
    const totalInvoices = invoiceData.length;

    // Calculate availability rate
    const availableItems = inventoryData.filter(item => 
      item.status === 'available' || item.status === 'good'
    ).length;
    const availabilityRate = totalInventoryItems > 0 ? (availableItems / totalInventoryItems) * 100 : 0;

    // Calculate utilization rate
    const deployedItems = inventoryData.filter(item => 
      item.status === 'deployed' || item.status === 'in_use'
    ).length;
    const utilizationRate = totalInventoryItems > 0 ? (deployedItems / totalInventoryItems) * 100 : 0;

    // Calculate damage rate
    const damagedItems = inventoryData.filter(item => 
      item.status === 'damaged' || item.status === 'needs_repair'
    ).length;
    const damageRate = totalInventoryItems > 0 ? (damagedItems / totalInventoryItems) * 100 : 0;

    // Calculate maintenance frequency
    const maintenanceItems = inventoryData.filter(item => 
      item.status === 'maintenance' || item.lastMaintenanceDate
    ).length;
    const maintenanceFrequency = totalInventoryItems > 0 ? (maintenanceItems / totalInventoryItems) * 100 : 0;

    // Calculate turnover rate (based on material requests)
    const daysInPeriod = moment(this.dateRange.endDate).diff(moment(this.dateRange.startDate), 'days') || 1;
    const turnoverRate = totalInventoryItems > 0 ? (totalMaterialRequests / totalInventoryItems) * (365 / daysInPeriod) : 0;

    // Calculate idle time percentage
    const idleItems = inventoryData.filter(item => 
      item.status === 'available' && !this.hasRecentActivity(item)
    ).length;
    const idleTimePercentage = totalInventoryItems > 0 ? (idleItems / totalInventoryItems) * 100 : 0;

    return {
      totalInventoryItems,
      totalMaterialRequests,
      totalSchedules,
      totalInvoices,
      availabilityRate: Math.round(availabilityRate * 100) / 100,
      utilizationRate: Math.round(utilizationRate * 100) / 100,
      damageRate: Math.round(damageRate * 100) / 100,
      maintenanceFrequency: Math.round(maintenanceFrequency * 100) / 100,
      turnoverRate: Math.round(turnoverRate * 100) / 100,
      idleTimePercentage: Math.round(idleTimePercentage * 100) / 100,
      daysInPeriod
    };
  }

  /**
   * Check if item has recent activity
   * @param {Object} item - Inventory item
   * @returns {boolean} Has recent activity
   */
  hasRecentActivity(item) {
    const thirtyDaysAgo = moment().subtract(30, 'days').toDate();
    
    return (
      (item.lastUsedDate && new Date(item.lastUsedDate) > thirtyDaysAgo) ||
      (item.lastMaintenanceDate && new Date(item.lastMaintenanceDate) > thirtyDaysAgo) ||
      (item.updatedAt && new Date(item.updatedAt) > thirtyDaysAgo)
    );
  }

  /**
   * Generate utilization insights
   * @param {Object} metrics - Utilization metrics
   * @returns {Array} Insights array
   */
  generateUtilizationInsights(metrics) {
    const insights = [];

    // Availability insights
    if (metrics.availabilityRate < 70) {
      insights.push({
        type: 'warning',
        category: 'availability',
        title: 'Low Availability Rate',
        message: `Only ${metrics.availabilityRate}% of inventory is available. Consider increasing inventory or improving maintenance processes.`,
        priority: 'high'
      });
    } else if (metrics.availabilityRate > 90) {
      insights.push({
        type: 'success',
        category: 'availability',
        title: 'Excellent Availability',
        message: `${metrics.availabilityRate}% availability rate indicates efficient inventory management.`,
        priority: 'low'
      });
    }

    // Utilization insights
    if (metrics.utilizationRate < 50) {
      insights.push({
        type: 'info',
        category: 'utilization',
        title: 'Low Utilization Rate',
        message: `${metrics.utilizationRate}% utilization suggests potential for inventory optimization or increased marketing efforts.`,
        priority: 'medium'
      });
    } else if (metrics.utilizationRate > 85) {
      insights.push({
        type: 'warning',
        category: 'utilization',
        title: 'High Utilization Rate',
        message: `${metrics.utilizationRate}% utilization indicates high demand. Consider expanding inventory to meet future needs.`,
        priority: 'high'
      });
    }

    // Damage rate insights
    if (metrics.damageRate > 15) {
      insights.push({
        type: 'error',
        category: 'damage',
        title: 'High Damage Rate',
        message: `${metrics.damageRate}% damage rate is concerning. Review handling procedures and maintenance schedules.`,
        priority: 'high'
      });
    } else if (metrics.damageRate < 5) {
      insights.push({
        type: 'success',
        category: 'damage',
        title: 'Low Damage Rate',
        message: `${metrics.damageRate}% damage rate indicates excellent equipment care and handling.`,
        priority: 'low'
      });
    }

    // Idle time insights
    if (metrics.idleTimePercentage > 30) {
      insights.push({
        type: 'warning',
        category: 'idle_time',
        title: 'High Idle Time',
        message: `${metrics.idleTimePercentage}% of inventory is idle. Consider marketing campaigns or inventory reduction.`,
        priority: 'medium'
      });
    }

    // Turnover insights
    if (metrics.turnoverRate < 2) {
      insights.push({
        type: 'info',
        category: 'turnover',
        title: 'Low Turnover Rate',
        message: `Turnover rate of ${metrics.turnoverRate.toFixed(1)} times per year suggests potential for increased utilization.`,
        priority: 'medium'
      });
    } else if (metrics.turnoverRate > 8) {
      insights.push({
        type: 'success',
        category: 'turnover',
        title: 'High Turnover Rate',
        message: `Excellent turnover rate of ${metrics.turnoverRate.toFixed(1)} times per year indicates efficient inventory management.`,
        priority: 'low'
      });
    }

    return insights;
  }

  /**
   * Get material type breakdown
   * @param {Array} inventoryData - Inventory data
   * @param {Array} materialListData - Material list data
   * @returns {Object} Material breakdown
   */
  getMaterialTypeBreakdown(inventoryData, materialListData) {
    const breakdown = {};

    // Process inventory data
    inventoryData.forEach(item => {
      const type = item.type || item.materialType || 'Unknown';
      if (!breakdown[type]) {
        breakdown[type] = {
          total: 0,
          available: 0,
          deployed: 0,
          damaged: 0,
          maintenance: 0,
          requests: 0
        };
      }

      breakdown[type].total++;
      
      switch (item.status) {
        case 'available':
        case 'good':
          breakdown[type].available++;
          break;
        case 'deployed':
        case 'in_use':
          breakdown[type].deployed++;
          break;
        case 'damaged':
        case 'needs_repair':
          breakdown[type].damaged++;
          break;
        case 'maintenance':
          breakdown[type].maintenance++;
          break;
      }
    });

    // Process material list requests
    materialListData.forEach(materialList => {
      if (materialList.requirements) {
        Object.entries(materialList.requirements).forEach(([type, requirement]) => {
          if (!breakdown[type]) {
            breakdown[type] = {
              total: 0,
              available: 0,
              deployed: 0,
              damaged: 0,
              maintenance: 0,
              requests: 0
            };
          }
          breakdown[type].requests += requirement.totalQuantity || 0;
        });
      }
    });

    return breakdown;
  }

  /**
   * Calculate utilization trends
   * @param {Object} metrics - Current metrics
   * @returns {Object} Trend analysis
   */
  calculateUtilizationTrends(metrics) {
    // This would typically compare with historical data
    // For now, provide basic trend indicators
    return {
      availabilityTrend: this.getTrendIndicator(metrics.availabilityRate, 75),
      utilizationTrend: this.getTrendIndicator(metrics.utilizationRate, 65),
      damageTrend: this.getTrendIndicator(metrics.damageRate, 10, true), // Lower is better
      turnoverTrend: this.getTrendIndicator(metrics.turnoverRate, 4),
      summary: 'Trends based on industry benchmarks. Historical comparison requires more data.'
    };
  }

  /**
   * Get trend indicator
   * @param {number} value - Current value
   * @param {number} benchmark - Benchmark value
   * @param {boolean} lowerIsBetter - Whether lower values are better
   * @returns {string} Trend indicator
   */
  getTrendIndicator(value, benchmark, lowerIsBetter = false) {
    const difference = value - benchmark;
    const threshold = benchmark * 0.1; // 10% threshold

    if (Math.abs(difference) < threshold) {
      return 'stable';
    }

    if (lowerIsBetter) {
      return difference < 0 ? 'improving' : 'declining';
    } else {
      return difference > 0 ? 'improving' : 'declining';
    }
  }

  /**
   * Generate recommendations
   * @param {Object} metrics - Utilization metrics
   * @param {Array} insights - Generated insights
   * @returns {Array} Recommendations
   */
  generateRecommendations(metrics, insights) {
    const recommendations = [];

    // High-priority recommendations based on insights
    const highPriorityInsights = insights.filter(insight => insight.priority === 'high');
    
    highPriorityInsights.forEach(insight => {
      switch (insight.category) {
        case 'availability':
          recommendations.push({
            title: 'Improve Inventory Availability',
            description: 'Increase maintenance frequency and consider expanding inventory for high-demand items.',
            priority: 'high',
            estimatedImpact: 'High',
            timeframe: '2-4 weeks'
          });
          break;
        case 'utilization':
          recommendations.push({
            title: 'Expand Inventory Capacity',
            description: 'High utilization indicates demand exceeds supply. Consider purchasing additional equipment.',
            priority: 'high',
            estimatedImpact: 'High',
            timeframe: '4-8 weeks'
          });
          break;
        case 'damage':
          recommendations.push({
            title: 'Review Handling Procedures',
            description: 'Implement staff training and improve equipment handling protocols to reduce damage rates.',
            priority: 'high',
            estimatedImpact: 'Medium',
            timeframe: '1-2 weeks'
          });
          break;
      }
    });

    // General recommendations based on metrics
    if (metrics.idleTimePercentage > 25) {
      recommendations.push({
        title: 'Optimize Inventory Mix',
        description: 'Consider reducing slow-moving inventory and focusing on high-demand items.',
        priority: 'medium',
        estimatedImpact: 'Medium',
        timeframe: '4-6 weeks'
      });
    }

    if (metrics.turnoverRate < 3) {
      recommendations.push({
        title: 'Increase Marketing Efforts',
        description: 'Low turnover suggests potential for increased demand through marketing and sales initiatives.',
        priority: 'medium',
        estimatedImpact: 'Medium',
        timeframe: '2-4 weeks'
      });
    }

    return recommendations;
  }
}

/**
 * Schedule Optimization Analytics
 * Analyzes schedule efficiency and optimization opportunities
 */
export class ScheduleOptimizationAnalytics {
  constructor(dateRange) {
    this.dateRange = dateRange;
  }

  /**
   * Generate schedule optimization report
   * @returns {Promise<Object>} Optimization report
   */
  async generateOptimizationReport() {
    try {
      console.log('📊 Generating schedule optimization report...');

      const [scheduleData, materialListData, invoiceData] = await Promise.all([
        this.getScheduleData(),
        this.getMaterialListData(),
        this.getInvoiceData()
      ]);

      const optimizationMetrics = this.calculateOptimizationMetrics(
        scheduleData,
        materialListData,
        invoiceData
      );

      const insights = this.generateOptimizationInsights(optimizationMetrics);

      return {
        period: this.dateRange.periodLabel,
        dateRange: this.dateRange,
        metrics: optimizationMetrics,
        insights,
        scheduleEfficiency: this.calculateScheduleEfficiency(scheduleData),
        conflictAnalysis: this.analyzeScheduleConflicts(scheduleData),
        recommendations: this.generateOptimizationRecommendations(optimizationMetrics, insights)
      };

    } catch (error) {
      console.error('❌ Error generating optimization report:', error);
      throw error;
    }
  }

  /**
   * Get schedule data for the period
   * @returns {Promise<Array>} Schedule data
   */
  async getScheduleData() {
    try {
      const scheduleQuery = query(
        collection(db, 'schedules'),
        where('createdAt', '>=', Timestamp.fromDate(this.dateRange.startDate)),
        where('createdAt', '<=', Timestamp.fromDate(this.dateRange.endDate)),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(scheduleQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error fetching schedule data:', error);
      return [];
    }
  }

  /**
   * Get material list data for the period
   * @returns {Promise<Array>} Material list data
   */
  async getMaterialListData() {
    try {
      const materialListQuery = query(
        collection(db, 'materialLists'),
        where('createdAt', '>=', Timestamp.fromDate(this.dateRange.startDate)),
        where('createdAt', '<=', Timestamp.fromDate(this.dateRange.endDate)),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(materialListQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error fetching material list data:', error);
      return [];
    }
  }

  /**
   * Get invoice data for the period
   * @returns {Promise<Array>} Invoice data
   */
  async getInvoiceData() {
    try {
      const invoiceQuery = query(
        collection(db, 'invoices'),
        where('createdAt', '>=', Timestamp.fromDate(this.dateRange.startDate)),
        where('createdAt', '<=', Timestamp.fromDate(this.dateRange.endDate)),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(invoiceQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error fetching invoice data:', error);
      return [];
    }
  }

  /**
   * Calculate optimization metrics
   * @param {Array} scheduleData - Schedule data
   * @param {Array} materialListData - Material list data
   * @param {Array} invoiceData - Invoice data
   * @returns {Object} Optimization metrics
   */
  calculateOptimizationMetrics(scheduleData, materialListData, invoiceData) {
    const totalSchedules = scheduleData.length;
    const completedSchedules = scheduleData.filter(s => s.status === 'completed').length;
    const cancelledSchedules = scheduleData.filter(s => s.status === 'cancelled').length;
    const delayedSchedules = scheduleData.filter(s => this.isDelayed(s)).length;

    const completionRate = totalSchedules > 0 ? (completedSchedules / totalSchedules) * 100 : 0;
    const cancellationRate = totalSchedules > 0 ? (cancelledSchedules / totalSchedules) * 100 : 0;
    const delayRate = totalSchedules > 0 ? (delayedSchedules / totalSchedules) * 100 : 0;

    // Calculate average lead time
    const leadTimes = scheduleData
      .filter(s => s.scheduledDate && s.createdAt)
      .map(s => moment(s.scheduledDate).diff(moment(s.createdAt), 'days'));
    
    const averageLeadTime = leadTimes.length > 0 
      ? leadTimes.reduce((sum, time) => sum + time, 0) / leadTimes.length 
      : 0;

    return {
      totalSchedules,
      completedSchedules,
      cancelledSchedules,
      delayedSchedules,
      completionRate: Math.round(completionRate * 100) / 100,
      cancellationRate: Math.round(cancellationRate * 100) / 100,
      delayRate: Math.round(delayRate * 100) / 100,
      averageLeadTime: Math.round(averageLeadTime * 100) / 100
    };
  }

  /**
   * Check if schedule is delayed
   * @param {Object} schedule - Schedule object
   * @returns {boolean} Is delayed
   */
  isDelayed(schedule) {
    if (!schedule.scheduledDate || !schedule.actualCompletionDate) {
      return false;
    }
    
    return moment(schedule.actualCompletionDate).isAfter(moment(schedule.scheduledDate));
  }

  /**
   * Calculate schedule efficiency
   * @param {Array} scheduleData - Schedule data
   * @returns {Object} Efficiency metrics
   */
  calculateScheduleEfficiency(scheduleData) {
    const jobTypeEfficiency = {};
    const timeSlotEfficiency = {};

    scheduleData.forEach(schedule => {
      const jobType = schedule.jobType || 'unknown';
      const timeSlot = this.getTimeSlot(schedule.scheduledDate);

      // Job type efficiency
      if (!jobTypeEfficiency[jobType]) {
        jobTypeEfficiency[jobType] = { total: 0, completed: 0, delayed: 0 };
      }
      jobTypeEfficiency[jobType].total++;
      if (schedule.status === 'completed') {
        jobTypeEfficiency[jobType].completed++;
      }
      if (this.isDelayed(schedule)) {
        jobTypeEfficiency[jobType].delayed++;
      }

      // Time slot efficiency
      if (!timeSlotEfficiency[timeSlot]) {
        timeSlotEfficiency[timeSlot] = { total: 0, completed: 0, delayed: 0 };
      }
      timeSlotEfficiency[timeSlot].total++;
      if (schedule.status === 'completed') {
        timeSlotEfficiency[timeSlot].completed++;
      }
      if (this.isDelayed(schedule)) {
        timeSlotEfficiency[timeSlot].delayed++;
      }
    });

    return {
      jobTypeEfficiency,
      timeSlotEfficiency
    };
  }

  /**
   * Get time slot for schedule
   * @param {string} scheduledDate - Scheduled date
   * @returns {string} Time slot
   */
  getTimeSlot(scheduledDate) {
    if (!scheduledDate) return 'unknown';
    
    const hour = moment(scheduledDate).hour();
    
    if (hour < 9) return 'early_morning';
    if (hour < 12) return 'morning';
    if (hour < 14) return 'lunch';
    if (hour < 17) return 'afternoon';
    return 'evening';
  }

  /**
   * Analyze schedule conflicts
   * @param {Array} scheduleData - Schedule data
   * @returns {Object} Conflict analysis
   */
  analyzeScheduleConflicts(scheduleData) {
    const conflicts = [];
    const conflictsByDate = {};

    // Group schedules by date
    scheduleData.forEach(schedule => {
      if (!schedule.scheduledDate) return;
      
      const dateKey = moment(schedule.scheduledDate).format('YYYY-MM-DD');
      if (!conflictsByDate[dateKey]) {
        conflictsByDate[dateKey] = [];
      }
      conflictsByDate[dateKey].push(schedule);
    });

    // Analyze conflicts for each date
    Object.entries(conflictsByDate).forEach(([date, daySchedules]) => {
      if (daySchedules.length > 1) {
        // Check for resource conflicts
        const resourceConflicts = this.findResourceConflicts(daySchedules);
        if (resourceConflicts.length > 0) {
          conflicts.push({
            date,
            type: 'resource_conflict',
            schedules: daySchedules,
            conflicts: resourceConflicts
          });
        }
      }
    });

    return {
      totalConflicts: conflicts.length,
      conflicts,
      conflictRate: scheduleData.length > 0 ? (conflicts.length / scheduleData.length) * 100 : 0
    };
  }

  /**
   * Find resource conflicts in day schedules
   * @param {Array} daySchedules - Schedules for a specific day
   * @returns {Array} Resource conflicts
   */
  findResourceConflicts(daySchedules) {
    const conflicts = [];
    
    // This would check for material/equipment conflicts
    // For now, return basic conflict detection
    if (daySchedules.length > 3) {
      conflicts.push({
        type: 'capacity_overload',
        message: `${daySchedules.length} schedules on same day may exceed capacity`
      });
    }

    return conflicts;
  }

  /**
   * Generate optimization insights
   * @param {Object} metrics - Optimization metrics
   * @returns {Array} Insights
   */
  generateOptimizationInsights(metrics) {
    const insights = [];

    if (metrics.completionRate < 80) {
      insights.push({
        type: 'warning',
        category: 'completion',
        title: 'Low Completion Rate',
        message: `${metrics.completionRate}% completion rate indicates scheduling or execution issues.`,
        priority: 'high'
      });
    }

    if (metrics.cancellationRate > 15) {
      insights.push({
        type: 'error',
        category: 'cancellation',
        title: 'High Cancellation Rate',
        message: `${metrics.cancellationRate}% cancellation rate suggests planning or communication issues.`,
        priority: 'high'
      });
    }

    if (metrics.delayRate > 20) {
      insights.push({
        type: 'warning',
        category: 'delays',
        title: 'High Delay Rate',
        message: `${metrics.delayRate}% of schedules are delayed. Review scheduling accuracy and resource allocation.`,
        priority: 'medium'
      });
    }

    if (metrics.averageLeadTime < 2) {
      insights.push({
        type: 'info',
        category: 'lead_time',
        title: 'Short Lead Time',
        message: `Average lead time of ${metrics.averageLeadTime.toFixed(1)} days may not allow adequate preparation.`,
        priority: 'medium'
      });
    }

    return insights;
  }

  /**
   * Generate optimization recommendations
   * @param {Object} metrics - Optimization metrics
   * @param {Array} insights - Generated insights
   * @returns {Array} Recommendations
   */
  generateOptimizationRecommendations(metrics, insights) {
    const recommendations = [];

    if (metrics.completionRate < 80) {
      recommendations.push({
        title: 'Improve Schedule Planning',
        description: 'Implement better resource planning and schedule validation to improve completion rates.',
        priority: 'high',
        estimatedImpact: 'High',
        timeframe: '2-4 weeks'
      });
    }

    if (metrics.cancellationRate > 15) {
      recommendations.push({
        title: 'Enhance Communication',
        description: 'Improve customer communication and confirmation processes to reduce cancellations.',
        priority: 'high',
        estimatedImpact: 'Medium',
        timeframe: '1-2 weeks'
      });
    }

    if (metrics.delayRate > 20) {
      recommendations.push({
        title: 'Optimize Resource Allocation',
        description: 'Review resource allocation and scheduling processes to reduce delays.',
        priority: 'medium',
        estimatedImpact: 'Medium',
        timeframe: '3-4 weeks'
      });
    }

    return recommendations;
  }
}

/**
 * Hybrid Material + Financial Analytics
 * Combines material usage data from both material lists and invoice line items
 * with comprehensive financial performance analysis
 */
export class HybridMaterialFinancialAnalytics {
  constructor(dateRange) {
    this.dateRange = dateRange;
  }

  /**
   * Generate hybrid material + financial analytics report
   * @returns {Promise<Object>} Comprehensive hybrid report
   */
  async generateHybridReport() {
    try {
      console.log('📊 Generating hybrid material + financial analytics report...');

      const invoiceData = await this.getInvoiceData();
      
      // Get hybrid material data (material lists + line items)
      const hybridMaterialData = await this.getHybridMaterialData(invoiceData);
      
      // Calculate financial metrics
      const financialMetrics = this.calculateFinancialMetrics(invoiceData, hybridMaterialData);
      
      // Generate insights
      const insights = this.generateHybridInsights(hybridMaterialData, financialMetrics);

      return {
        period: this.dateRange.periodLabel,
        dateRange: this.dateRange,
        materialData: hybridMaterialData,
        financialMetrics,
        insights,
        dataSourceBreakdown: this.calculateDataSourceBreakdown(invoiceData),
        recommendations: this.generateHybridRecommendations(hybridMaterialData, financialMetrics, insights)
      };

    } catch (error) {
      console.error('❌ Error generating hybrid analytics report:', error);
      throw error;
    }
  }

  /**
   * Get invoice data for the period
   * @returns {Promise<Array>} Invoice data
   */
  async getInvoiceData() {
    try {
      const invoiceQuery = query(
        collection(db, 'invoices'),
        where('createdAt', '>=', Timestamp.fromDate(this.dateRange.startDate)),
        where('createdAt', '<=', Timestamp.fromDate(this.dateRange.endDate)),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(invoiceQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('❌ Error fetching invoice data:', error);
      return [];
    }
  }

  /**
   * Get hybrid material data from invoices (material lists + line items)
   * @param {Array} invoices - Invoice data
   * @returns {Promise<Object>} Hybrid material data
   */
  async getHybridMaterialData(invoices) {
    const materialData = {};
    let materialListCount = 0;
    let lineItemCount = 0;

    for (const invoice of invoices) {
      try {
        // Strategy 1: Check if invoice has material list (PRIORITY)
        const materialList = await getMaterialListByInvoice(invoice.id);
        
        if (materialList && materialList.requirements) {
          // Use detailed material list data (preferred)
          materialListCount++;
          Object.entries(materialList.requirements).forEach(([materialType, requirement]) => {
            this.addToMaterialData(materialData, materialType, requirement, invoice, 'materialList');
          });
          
        } else if (invoice.lineItems && invoice.lineItems.length > 0) {
          // Strategy 2: Calculate from line items using existing logic
          lineItemCount++;
          const calculatedRequirements = calculateMaterialRequirements(invoice.lineItems);
          
          Object.entries(calculatedRequirements.requirements).forEach(([materialType, requirement]) => {
            // Convert line item data to material requirement format
            const materialRequirement = {
              materialType,
              totalQuantity: requirement.totalQuantity,
              configurations: requirement.configurations
            };
            this.addToMaterialData(materialData, materialType, materialRequirement, invoice, 'lineItems');
          });
        }
      } catch (error) {
        console.error(`❌ Error processing invoice ${invoice.id}:`, error);
      }
    }

    // Calculate derived metrics for each material
    Object.values(materialData).forEach(data => {
      data.averagePrice = data.totalQuantity > 0 ? data.totalRevenue / data.totalQuantity : 0;
      data.revenuePerUnit = data.averagePrice;
      data.profitMargin = this.calculateProfitMargin(data);
    });

    return {
      materials: materialData,
      dataSourceStats: {
        totalInvoices: invoices.length,
        materialListCount,
        lineItemCount,
        materialListCoverage: invoices.length > 0 ? (materialListCount / invoices.length) * 100 : 0
      }
    };
  }

  /**
   * Add material data to the aggregated dataset
   * @param {Object} materialData - Aggregated material data
   * @param {string} materialType - Material type
   * @param {Object} requirement - Material requirement
   * @param {Object} invoice - Invoice data
   * @param {string} dataSource - Data source ('materialList' or 'lineItems')
   */
  addToMaterialData(materialData, materialType, requirement, invoice, dataSource) {
    if (!materialData[materialType]) {
      materialData[materialType] = {
        materialType,
        totalQuantity: 0,
        totalRevenue: 0,
        averagePrice: 0,
        invoiceCount: 0,
        profitMargin: 0,
        revenuePerUnit: 0,
        fromMaterialLists: 0,
        fromLineItems: 0,
        revenueByType: {
          purchase: 0,
          rental: 0,
          rental_extension: 0,
          scaffolding_service: 0,
          damaged_items: 0
        },
        revenueByStatus: {
          paid: 0,
          pending: 0,
          draft: 0
        },
        dataSource: 'hybrid'
      };
    }

    const quantity = requirement.totalQuantity || 0;
    const revenue = this.calculateMaterialRevenue(invoice, materialType, requirement);

    materialData[materialType].totalQuantity += quantity;
    materialData[materialType].totalRevenue += revenue;
    materialData[materialType].invoiceCount++;

    // Track data source
    if (dataSource === 'materialList') {
      materialData[materialType].fromMaterialLists += quantity;
    } else {
      materialData[materialType].fromLineItems += quantity;
    }

    // Track by invoice type
    const invoiceType = invoice.invoiceType || 'purchase';
    materialData[materialType].revenueByType[invoiceType] += revenue;

    // Track by payment status
    const statusCategory = this.getStatusCategory(invoice.status);
    materialData[materialType].revenueByStatus[statusCategory] += revenue;
  }

  /**
   * Calculate material revenue from invoice
   * @param {Object} invoice - Invoice data
   * @param {string} materialType - Material type
   * @param {Object} requirement - Material requirement
   * @returns {number} Calculated revenue
   */
  calculateMaterialRevenue(invoice, materialType, requirement) {
    // If we have detailed pricing from material list, use it (PRIORITY)
    if (requirement.unitPrice && requirement.totalQuantity) {
      return requirement.unitPrice * requirement.totalQuantity;
    }

    // If we have configurations with pricing, calculate from those
    if (requirement.configurations && Array.isArray(requirement.configurations)) {
      let totalRevenue = 0;
      requirement.configurations.forEach(config => {
        if (config.unitPrice && config.quantity) {
          totalRevenue += config.unitPrice * config.quantity;
        }
      });
      if (totalRevenue > 0) {
        return totalRevenue;
      }
    }

    // Try to find matching line items for this material type
    if (invoice.lineItems && invoice.lineItems.length > 0) {
      let materialRevenue = 0;
      
      invoice.lineItems.forEach(item => {
        // Check if this line item matches the material type
        if (this.isLineItemForMaterial(item, materialType)) {
          const itemTotal = (item.unitPrice || 0) * (item.quantity || 1);
          materialRevenue += itemTotal;
        }
      });
      
      if (materialRevenue > 0) {
        return materialRevenue;
      }
    }

    // Last resort: estimate based on material type pricing
    const estimatedUnitPrice = this.getEstimatedMaterialPrice(materialType, invoice.invoiceType);
    const quantity = requirement.totalQuantity || 1;
    
    return estimatedUnitPrice * quantity;
  }

  /**
   * Check if line item corresponds to a specific material type
   * @param {Object} lineItem - Line item from invoice
   * @param {string} materialType - Material type to match
   * @returns {boolean} Whether line item matches material type
   */
  isLineItemForMaterial(lineItem, materialType) {
    const itemDescription = (lineItem.description || '').toLowerCase();
    const itemName = (lineItem.name || '').toLowerCase();
    
    // Material type mapping for line item matching
    const materialKeywords = {
      'SW_FRAME': ['single width frame', 'sw frame', 'single frame'],
      'DW_FRAME': ['double width frame', 'dw frame', 'double frame'],
      'SW_PLATFORM': ['single width platform', 'sw platform', 'single platform'],
      'DW_PLATFORM': ['double width platform', 'dw platform', 'double platform'],
      'HORIZONTAL_BRACE': ['horizontal brace', 'h brace', 'horizontal'],
      'DIAGONAL_BRACE': ['diagonal brace', 'd brace', 'diagonal'],
      'OUTRIGGER_2M': ['outrigger 2m', '2m outrigger', 'outrigger 2'],
      'OUTRIGGER_3M': ['outrigger 3m', '3m outrigger', 'outrigger 3'],
      'OUTRIGGER_4M': ['outrigger 4m', '4m outrigger', 'outrigger 4'],
      'OUTRIGGER_5M': ['outrigger 5m', '5m outrigger', 'outrigger 5']
    };

    const keywords = materialKeywords[materialType] || [];
    
    return keywords.some(keyword => 
      itemDescription.includes(keyword) || itemName.includes(keyword)
    );
  }

  /**
   * Get estimated material price based on type and invoice type
   * @param {string} materialType - Material type
   * @param {string} invoiceType - Invoice type (rental, purchase, etc.)
   * @returns {number} Estimated unit price
   */
  getEstimatedMaterialPrice(materialType, invoiceType = 'rental') {
    // Base pricing for different material types
    const basePricing = {
      'SW_FRAME': { rental: 8, purchase: 120 },
      'DW_FRAME': { rental: 12, purchase: 180 },
      'SW_PLATFORM': { rental: 6, purchase: 90 },
      'DW_PLATFORM': { rental: 9, purchase: 135 },
      'HORIZONTAL_BRACE': { rental: 3, purchase: 45 },
      'DIAGONAL_BRACE': { rental: 3, purchase: 45 },
      'OUTRIGGER_2M': { rental: 15, purchase: 225 },
      'OUTRIGGER_3M': { rental: 18, purchase: 270 },
      'OUTRIGGER_4M': { rental: 21, purchase: 315 },
      'OUTRIGGER_5M': { rental: 24, purchase: 360 }
    };

    const materialPricing = basePricing[materialType];
    if (!materialPricing) {
      // Default pricing for unknown materials
      return invoiceType === 'purchase' ? 100 : 10;
    }

    // Return appropriate price based on invoice type
    switch (invoiceType) {
      case 'purchase':
        return materialPricing.purchase;
      case 'rental':
      case 'rental_extension':
        return materialPricing.rental;
      case 'scaffolding_service':
        return materialPricing.rental * 1.5; // Service premium
      default:
        return materialPricing.rental;
    }
  }

  /**
   * Get status category for revenue tracking
   * @param {string} status - Invoice status
   * @returns {string} Status category
   */
  getStatusCategory(status) {
    switch (status) {
      case 'paid':
        return 'paid';
      case 'sent':
      case 'overdue':
        return 'pending';
      default:
        return 'draft';
    }
  }

  /**
   * Calculate profit margin (simplified)
   * @param {Object} materialData - Material data
   * @returns {number} Estimated profit margin percentage
   */
  calculateProfitMargin(materialData) {
    // Simplified profit margin calculation
    // In reality, you'd have actual cost data
    const estimatedCostRatio = {
      'SW_FRAME': 0.6,
      'DW_FRAME': 0.65,
      'SW_PLATFORM': 0.55,
      'DW_PLATFORM': 0.6,
      'HORIZONTAL_BRACE': 0.5,
      'DIAGONAL_BRACE': 0.5,
      'OUTRIGGER_2M': 0.7,
      'OUTRIGGER_3M': 0.7,
      'OUTRIGGER_4M': 0.7,
      'OUTRIGGER_5M': 0.7
    };

    const costRatio = estimatedCostRatio[materialData.materialType] || 0.6;
    return (1 - costRatio) * 100; // Convert to percentage
  }

  /**
   * Calculate financial metrics
   * @param {Array} invoices - Invoice data
   * @param {Object} hybridMaterialData - Hybrid material data
   * @returns {Object} Financial metrics
   */
  calculateFinancialMetrics(invoices, hybridMaterialData) {
    const totalRevenue = invoices.reduce((sum, invoice) => sum + (invoice.grandTotal || 0), 0);
    const paidRevenue = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.grandTotal || 0), 0);
    const pendingRevenue = invoices.filter(i => ['sent', 'overdue'].includes(i.status)).reduce((sum, i) => sum + (i.grandTotal || 0), 0);

    // Revenue by invoice type
    const revenueByType = {};
    invoices.forEach(invoice => {
      const type = invoice.invoiceType || 'purchase';
      if (!revenueByType[type]) {
        revenueByType[type] = 0;
      }
      revenueByType[type] += invoice.grandTotal || 0;
    });

    // Top performing scaffold products (from actual line items)
    const topMaterials = this.calculateTopPerformingProducts(invoices);

    return {
      totalRevenue,
      paidRevenue,
      pendingRevenue,
      paymentRate: totalRevenue > 0 ? (paidRevenue / totalRevenue) * 100 : 0,
      revenueByType,
      topMaterials,
      averageInvoiceValue: invoices.length > 0 ? totalRevenue / invoices.length : 0,
      totalInvoices: invoices.length
    };
  }

  /**
   * Calculate top performing scaffold products from invoice line items
   * @param {Array} invoices - Invoice data
   * @returns {Array} Top performing products
   */
  calculateTopPerformingProducts(invoices) {
    const productRevenue = {};

    invoices.forEach(invoice => {
      if (invoice.lineItems && invoice.lineItems.length > 0) {
        invoice.lineItems.forEach(item => {
          const productName = this.getProductDisplayName(item);
          const revenue = (item.unitPrice || 0) * (item.quantity || 1);
          const quantity = item.quantity || 1;

          if (!productRevenue[productName]) {
            productRevenue[productName] = {
              productName,
              revenue: 0,
              quantity: 0,
              invoiceCount: 0,
              revenuePerUnit: 0,
              materialEfficiency: 0,
              estimatedMaterialCost: 0
            };
          }

          productRevenue[productName].revenue += revenue;
          productRevenue[productName].quantity += quantity;
          productRevenue[productName].invoiceCount++;
        });
      }
    });

    // Calculate metrics for each product
    Object.values(productRevenue).forEach(product => {
      product.revenuePerUnit = product.quantity > 0 ? product.revenue / product.quantity : 0;
      
      // Estimate material cost based on scaffold type and dimensions
      product.estimatedMaterialCost = this.estimateScaffoldMaterialCost(product.productName);
      
      // Calculate material efficiency (revenue per dollar of material cost)
      product.materialEfficiency = product.estimatedMaterialCost > 0 
        ? product.revenuePerUnit / product.estimatedMaterialCost 
        : 0;
    });

    // Return top 5 products by revenue
    return Object.values(productRevenue)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5)
      .map(product => ({
        materialType: product.productName, // Keep same property name for compatibility
        revenue: product.revenue,
        quantity: product.quantity,
        revenuePerUnit: product.revenuePerUnit,
        invoiceCount: product.invoiceCount,
        materialEfficiency: product.materialEfficiency,
        estimatedMaterialCost: product.estimatedMaterialCost
      }));
  }

  /**
   * Calculate actual material cost for scaffold products using exact configurations
   * @param {string} productName - Product name
   * @returns {number} Actual material cost based on inventory calculations
   */
  estimateScaffoldMaterialCost(productName) {
    const productLower = productName.toLowerCase();
    
    // Import the actual scaffold configurations
    const { SCAFFOLD_CONFIGURATIONS } = require('./inventoryCalculations');
    
    // Base material costs for different scaffold components (actual inventory costs)
    const baseCosts = {
      'SW_FRAME': 120,
      'DW_FRAME': 180,
      'ENTRY_FRAME': 150,
      'SW_PLATFORM': 90,
      'DW_PLATFORM': 135,
      'LANDING_PLATFORM': 100,
      'HORIZONTAL_BRACE': 45,
      'DIAGONAL_BRACE': 45,
      'NARROW_BRACE': 40,
      'STAIRS_LADDER_5': 200,
      'STAIRS_LADDER_8': 250,
      'HANDRAIL': 60,
      'SW_TOEBOARD': 30,
      'DW_TOEBOARD': 45,
      'SW_GUARDFRAME': 80,
      'DW_GUARDFRAME': 120,
      'BLUE_CAP': 15,
      'WHEELS': 80,
      'JACKBASE': 70,
      'SAFETY_CLIP': 5,
      'OUTRIGGER_2M': 225,
      'OUTRIGGER_3M': 270,
      'OUTRIGGER_4M': 315,
      'OUTRIGGER_5M': 360
    };

    // Try to match the product name to a scaffold configuration
    let matchedConfig = null;
    
    // Extract height and width from product name
    let height = null;
    let width = null;
    
    // Extract height (platform height, not working height)
    const heightMatches = productLower.match(/(\d+)\.?(\d+)?m.*height.*(\d+)\.?(\d+)?m/);
    if (heightMatches) {
      // Platform height is typically working height - 2m
      const workingHeight = parseFloat(heightMatches[3] + (heightMatches[4] ? '.' + heightMatches[4] : ''));
      height = workingHeight - 2;
    } else {
      // Try to extract from simpler patterns
      const simpleHeightMatch = productLower.match(/(\d+)m/);
      if (simpleHeightMatch) {
        height = parseInt(simpleHeightMatch[1]);
        // If it's a working height, convert to platform height
        if (height > 10) height = height - 2;
      }
    }
    
    // Extract width
    if (productLower.includes('single width')) {
      width = 'single';
    } else if (productLower.includes('double width')) {
      width = 'double';
    }
    
    // Find matching configuration
    if (height && width) {
      const configId = `${width}-width-${height}m`;
      matchedConfig = SCAFFOLD_CONFIGURATIONS[configId];
    }
    
    // If we found a matching configuration, calculate exact cost
    if (matchedConfig && matchedConfig.requiredMaterials) {
      let totalCost = 0;
      
      matchedConfig.requiredMaterials.forEach(material => {
        const materialCost = baseCosts[material.materialType] || 50; // Default cost if not found
        totalCost += materialCost * material.quantity;
      });
      
      return Math.round(totalCost);
    }
    
    // Fallback: estimate based on patterns in product name
    let estimatedCost = 0;
    
    // Base costs for common scaffold types
    if (productLower.includes('single width')) {
      if (productLower.includes('4m') || productLower.includes('5m')) {
        estimatedCost = 1200; // Based on single-width-4m/5m configurations
      } else if (productLower.includes('6m') || productLower.includes('7m')) {
        estimatedCost = 1800; // Based on single-width-6m/7m configurations
      } else if (productLower.includes('8m') || productLower.includes('9m')) {
        estimatedCost = 2400; // Based on single-width-8m configurations
      } else {
        estimatedCost = 1500; // Default single width
      }
    } else if (productLower.includes('double width')) {
      if (productLower.includes('4m') || productLower.includes('5m')) {
        estimatedCost = 2400; // Based on double-width-4m/5m configurations
      } else if (productLower.includes('6m') || productLower.includes('7m')) {
        estimatedCost = 3600; // Based on double-width-6m/7m configurations
      } else if (productLower.includes('8m') || productLower.includes('9m')) {
        estimatedCost = 4800; // Based on double-width-8m/9m configurations
      } else {
        estimatedCost = 3000; // Default double width
      }
    } else {
      estimatedCost = 1000; // Default for unknown types
    }
    
    return Math.round(estimatedCost);
  }

  /**
   * Calculate material efficiency rankings
   * @param {Array} invoices - Invoice data
   * @returns {Array} Products ranked by material efficiency
   */
  calculateMaterialEfficiencyRankings(invoices) {
    const productRevenue = {};

    invoices.forEach(invoice => {
      if (invoice.lineItems && invoice.lineItems.length > 0) {
        invoice.lineItems.forEach(item => {
          const productName = this.getProductDisplayName(item);
          const revenue = (item.unitPrice || 0) * (item.quantity || 1);
          const quantity = item.quantity || 1;

          if (!productRevenue[productName]) {
            productRevenue[productName] = {
              productName,
              revenue: 0,
              quantity: 0,
              invoiceCount: 0,
              revenuePerUnit: 0,
              materialEfficiency: 0,
              estimatedMaterialCost: 0
            };
          }

          productRevenue[productName].revenue += revenue;
          productRevenue[productName].quantity += quantity;
          productRevenue[productName].invoiceCount++;
        });
      }
    });

    // Calculate metrics for each product
    Object.values(productRevenue).forEach(product => {
      product.revenuePerUnit = product.quantity > 0 ? product.revenue / product.quantity : 0;
      product.estimatedMaterialCost = this.estimateScaffoldMaterialCost(product.productName);
      product.materialEfficiency = product.estimatedMaterialCost > 0 
        ? product.revenuePerUnit / product.estimatedMaterialCost 
        : 0;
    });

    // Return top 5 products by material efficiency
    return Object.values(productRevenue)
      .filter(product => product.quantity > 0)
      .sort((a, b) => b.materialEfficiency - a.materialEfficiency)
      .slice(0, 5)
      .map(product => ({
        materialType: product.productName,
        revenue: product.revenue,
        quantity: product.quantity,
        revenuePerUnit: product.revenuePerUnit,
        invoiceCount: product.invoiceCount,
        materialEfficiency: product.materialEfficiency,
        estimatedMaterialCost: product.estimatedMaterialCost,
        efficiencyRatio: `${product.materialEfficiency.toFixed(2)}x`
      }));
  }

  /**
   * Get user-friendly product display name from line item
   * @param {Object} lineItem - Invoice line item
   * @returns {string} Product display name
   */
  getProductDisplayName(lineItem) {
    // Use the actual description/name from the line item
    const description = lineItem.description || lineItem.name || 'Unknown Product';
    
    // Clean up common variations and make it more readable
    return description
      .replace(/\b(sw|dw)\b/gi, (match) => match.toUpperCase()) // SW/DW to uppercase
      .replace(/\b(\d+)m\b/gi, '$1M') // 5m to 5M
      .replace(/\bframe\b/gi, 'Frame')
      .replace(/\bplatform\b/gi, 'Platform')
      .replace(/\bbrace\b/gi, 'Brace')
      .replace(/\boutrigger\b/gi, 'Outrigger')
      .replace(/\bsingle\s*width\b/gi, 'Single Width')
      .replace(/\bdouble\s*width\b/gi, 'Double Width')
      .replace(/\bhorizontal\b/gi, 'Horizontal')
      .replace(/\bdiagonal\b/gi, 'Diagonal')
      .trim();
  }

  /**
   * Calculate data source breakdown
   * @param {Array} invoices - Invoice data
   * @returns {Object} Data source breakdown
   */
  calculateDataSourceBreakdown(invoices) {
    let materialListCount = 0;
    let lineItemCount = 0;
    let noDataCount = 0;

    invoices.forEach(invoice => {
      if (invoice.materialListId) {
        materialListCount++;
      } else if (invoice.lineItems && invoice.lineItems.length > 0) {
        lineItemCount++;
      } else {
        noDataCount++;
      }
    });

    const total = invoices.length;

    return {
      materialListCount,
      lineItemCount,
      noDataCount,
      total,
      materialListPercentage: total > 0 ? (materialListCount / total) * 100 : 0,
      lineItemPercentage: total > 0 ? (lineItemCount / total) * 100 : 0,
      migrationProgress: total > 0 ? (materialListCount / total) * 100 : 0
    };
  }

  /**
   * Generate hybrid insights
   * @param {Object} hybridMaterialData - Hybrid material data
   * @param {Object} financialMetrics - Financial metrics
   * @returns {Array} Insights array
   */
  generateHybridInsights(hybridMaterialData, financialMetrics) {
    const insights = [];

    // Data quality insights
    const materialListCoverage = hybridMaterialData.dataSourceStats.materialListCoverage;
    if (materialListCoverage < 50) {
      insights.push({
        type: 'info',
        category: 'data_quality',
        title: 'Low Material List Adoption',
        message: `Only ${materialListCoverage.toFixed(1)}% of invoices use material lists. Consider migrating more invoices for better accuracy.`,
        priority: 'medium'
      });
    } else if (materialListCoverage > 80) {
      insights.push({
        type: 'success',
        category: 'data_quality',
        title: 'Excellent Material List Adoption',
        message: `${materialListCoverage.toFixed(1)}% of invoices use material lists, providing high-quality analytics data.`,
        priority: 'low'
      });
    }

    // Revenue insights
    if (financialMetrics.topMaterials.length > 0) {
      const topMaterial = financialMetrics.topMaterials[0];
      const revenuePercentage = (topMaterial.revenue / financialMetrics.totalRevenue) * 100;
      
      insights.push({
        type: 'success',
        category: 'revenue',
        title: `Top Revenue Material: ${topMaterial.materialType}`,
        message: `${topMaterial.materialType} generated $${topMaterial.revenue.toLocaleString()} (${revenuePercentage.toFixed(1)}% of total revenue)`,
        priority: 'high'
      });
    }

    // Payment insights
    if (financialMetrics.paymentRate < 70) {
      insights.push({
        type: 'warning',
        category: 'payments',
        title: 'Low Payment Rate',
        message: `Only ${financialMetrics.paymentRate.toFixed(1)}% of revenue has been collected. Focus on payment collection.`,
        priority: 'high'
      });
    }

    // Outstanding payments
    if (financialMetrics.pendingRevenue > 10000) {
      insights.push({
        type: 'error',
        category: 'cash_flow',
        title: 'High Outstanding Payments',
        message: `$${financialMetrics.pendingRevenue.toLocaleString()} in pending payments. Prioritize collection efforts.`,
        priority: 'high'
      });
    }

    return insights;
  }

  /**
   * Generate hybrid recommendations
   * @param {Object} hybridMaterialData - Hybrid material data
   * @param {Object} financialMetrics - Financial metrics
   * @param {Array} insights - Generated insights
   * @returns {Array} Recommendations array
   */
  generateHybridRecommendations(hybridMaterialData, financialMetrics, insights) {
    const recommendations = [];

    // Data quality recommendations
    if (hybridMaterialData.dataSourceStats.materialListCoverage < 70) {
      recommendations.push({
        title: 'Improve Data Quality',
        description: 'Migrate more invoices to use material lists for better analytics accuracy and inventory tracking.',
        priority: 'medium',
        estimatedImpact: 'High',
        timeframe: '4-6 weeks'
      });
    }

    // Revenue optimization
    if (financialMetrics.topMaterials.length > 0) {
      const lowPerformers = Object.entries(hybridMaterialData.materials)
        .filter(([_, data]) => data.revenuePerUnit < 50)
        .sort((a, b) => a[1].revenuePerUnit - b[1].revenuePerUnit);

      if (lowPerformers.length > 0) {
        recommendations.push({
          title: 'Review Material Pricing',
          description: `${lowPerformers.length} materials earning less than $50 per unit. Consider pricing review or inventory optimization.`,
          priority: 'medium',
          estimatedImpact: 'Medium',
          timeframe: '2-3 weeks'
        });
      }
    }

    // Cash flow recommendations
    if (financialMetrics.pendingRevenue > 5000) {
      recommendations.push({
        title: 'Improve Payment Collection',
        description: 'Implement better payment tracking and collection processes to reduce outstanding amounts.',
        priority: 'high',
        estimatedImpact: 'High',
        timeframe: '1-2 weeks'
      });
    }

    return recommendations;
  }
}

/**
 * Generate comprehensive analytics report
 * @param {string} period - Time period
 * @param {Object} customRange - Custom date range
 * @returns {Promise<Object>} Comprehensive report
 */
export const generateComprehensiveAnalytics = async (period = TIME_PERIODS.LAST_30_DAYS, customRange = null) => {
  try {
    console.log('📊 Generating comprehensive analytics report...');

    const dateRange = getAnalyticsDateRange(period, customRange);

    const [utilizationAnalytics, scheduleAnalytics, hybridAnalytics] = await Promise.all([
      new MaterialUtilizationAnalytics(dateRange).generateUtilizationReport(),
      new ScheduleOptimizationAnalytics(dateRange).generateOptimizationReport(),
      new HybridMaterialFinancialAnalytics(dateRange).generateHybridReport()
    ]);

    return {
      period: dateRange.periodLabel,
      dateRange,
      utilization: utilizationAnalytics,
      scheduleOptimization: scheduleAnalytics,
      hybridMaterialFinancial: hybridAnalytics,
      summary: {
        totalInsights: utilizationAnalytics.insights.length + scheduleAnalytics.insights.length + hybridAnalytics.insights.length,
        highPriorityIssues: [
          ...utilizationAnalytics.insights.filter(i => i.priority === 'high'),
          ...scheduleAnalytics.insights.filter(i => i.priority === 'high'),
          ...hybridAnalytics.insights.filter(i => i.priority === 'high')
        ].length,
        totalRecommendations: utilizationAnalytics.recommendations.length + scheduleAnalytics.recommendations.length + hybridAnalytics.recommendations.length,
        totalRevenue: hybridAnalytics.financialMetrics.totalRevenue,
        materialListCoverage: hybridAnalytics.dataSourceBreakdown.materialListPercentage
      },
      generatedAt: new Date().toISOString()
    };

  } catch (error) {
    console.error('❌ Error generating comprehensive analytics:', error);
    throw error;
  }
};
